import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { MRT_SortingState } from 'material-react-table';
import {
	fetchApplicationsList,
	fetchApplicationSummary,
	IApplicationFilters,
} from '../../store/merchnts/actions/application';
import { TablesNames } from '../../store/tables/tables-context';
import { Table } from '../table/Table';
import { IPreparedApplicationData } from '../../store/tables/account/applications-data';
import { ApplicationsListHeader } from './ApplicationsListHeader';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { useLocation } from 'react-router';
import { fetchIntegrationPartnersList, fetchWhiteListedPartnersList } from '../../store/merchnts/actions/partners';
import { IPaginationData } from '../../interfaces/funds';

import './ApplicationsList.scss';

export const ApplicationsList = (): JSX.Element => {
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const location = useLocation();

	const navigate = useNavigate();
	const { merchantState: { isPartnerNamesListLoading }, dispatch } = useMerchants();

	const [statuses, setStatuses] = useState<string[]>([]);
	const [riskStatuses, setRiskStatuses] = useState<string[]>([]);
	const [operationsStatuses, setOperationsStatuses] = useState<string[]>([]);
	const [query, setQuery] = useState<string>('');
	const [partnerId, setPartnerId] = useState<string>('');
	const [integrationPartnerId, setIntegrationPartnerId] = useState<string>('');

	const [applicationsListData, setApplicationsListData] = useState<IPaginationData>(null);
	const [isApplicationsListsDataLoading, setIsApplicationsListsDataLoading] = useState<boolean>(true);

	const [sort, setSort] = useState<MRT_SortingState>([]);

	useEffect(() => {
		if (checkRoles(authUser, CLAIMS.SUPER_ADMIN)) {
			void fetchWhiteListedPartnersList(dispatch);
		}
		if (checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN)) {
			void fetchIntegrationPartnersList(dispatch);
		}
	}, []);

	const getSearchParams = (search: IApplicationFilters): IApplicationFilters => {
		let searchParams: IApplicationFilters = {};

		if (Object.keys(search).length) {
			searchParams = { ...search };

			// Set local state from URL parameters
			if (Array.isArray(searchParams?.statuses)) {
				setStatuses(searchParams.statuses);
			} else if (searchParams?.statuses) {
				setStatuses([searchParams.statuses]);
			} else {
				setStatuses([]);
			}

			if (Array.isArray(searchParams?.riskStatuses)) {
				setRiskStatuses(searchParams.riskStatuses);
			} else if (searchParams?.riskStatuses) {
				setRiskStatuses([searchParams.riskStatuses]);
			} else {
				setRiskStatuses([]);
			}

			if (Array.isArray(searchParams?.operationsStatuses)) {
				setOperationsStatuses(searchParams.operationsStatuses);
			} else if (searchParams?.operationsStatuses) {
				setOperationsStatuses([searchParams.operationsStatuses]);
			} else {
				setOperationsStatuses([]);
			}

			setQuery(searchParams?.query ?? '');
			setPartnerId(searchParams?.partnerId ?? '');
			setIntegrationPartnerId(searchParams?.integrationPartnerId ?? '');
		} else {
			// Default values when no search params
			searchParams = {
				pageNumber: 1,
				pageSize: 10,
			};
		}

		return searchParams;
	};

	const preparedTableData: IPreparedApplicationData[] = applicationsListData?.rows?.map((el): IPreparedApplicationData => ({
		id: el?.id,
		applicationName: el?.legalEntityName,
		industry: el?.industry,
		agreeToTerms: el?.agreeToTerms,
		createdOn: el?.createdOn,
		contactName: `${el?.primaryContact?.firstName} ${el?.primaryContact?.lastName}`,
		email: el?.primaryContact?.email,
		phone: el?.primaryContact?.phone,
		status: el?.statusName,
		operationsStatus: el?.operationsStatus,
		riskStatus: el?.riskStatus,
		partnerId: el?.partner?.id,
	}));

	const rowAction = (rowData): JSX.Element => {
		return (
			<div
				className="text-muted cursor-pointer text-center fw-bolder fs-7"
				onClick={(): void => navigate(
					`/accounts/applications/${rowData?.original?.id}/review`, {
						state: { search: location?.search },
					})}
			>
				View
			</div>
		);
	};

	const listener = async (search: IApplicationFilters): Promise<void> => {
		setIsApplicationsListsDataLoading(true);

		const searchParams = getSearchParams(search);

		const data = await fetchApplicationsList(searchParams);

		setApplicationsListData(data);

		setIsApplicationsListsDataLoading(false);

		await fetchApplicationSummary(dispatch, searchParams);
	};

	return (
		<>
			<ApplicationsListHeader
				statuses={statuses}
				setStatuses={setStatuses}
				riskStatuses={riskStatuses}
				setRiskStatuses={setRiskStatuses}
				operationsStatuses={operationsStatuses}
				setOperationsStatuses={setOperationsStatuses}
				query={query}
				setQuery={setQuery}
				partnerId={partnerId}
				setPartnerId={setPartnerId}
				sort={sort}
				integrationPartnerId={integrationPartnerId}
				setIntegrationPartnerId={setIntegrationPartnerId}
			/>

			<Table
				name={TablesNames.applicationsTableData}
				data={preparedTableData || []}
				sorting={sort}
				isLoading={isApplicationsListsDataLoading || isPartnerNamesListLoading}
				totalItemCount={applicationsListData?.totalItemCount}
				setSorting={(value): void => setSort(value)}
        enableRowActions={checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN)}
        rowActions={rowAction}
				actionColumnsSize={60}
				queryParamsListener={listener}
			/>
		</>
	);
};
